import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { X, Plus, Upload } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/utils/currency";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AddOrderModal({ onClose, onOrderCreated }) {
  const [formData, setFormData] = useState({
    client_name: "",
    email: "",
    contact_number: "",
    deadline: "",
    printing_method: "Sublimation",
    product_type: "Polo",
    kind_of_fabric: "Cotton",
    collar_type: "Regular",
    placket_style: "2-Button",
    button_color: "Black",
    photo: null,
  });

  const [items, setItems] = useState([
    { description: "", quantity: 1, price_per_unit: 0 },
  ]);

  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downPayment, setDownPayment] = useState(0);
  const [downPaymentDate, setDownPaymentDate] = useState("");
  const [modeOfPayment, setModeOfPayment] = useState("");
  const [productionNotes, setProductionNotes] = useState("");

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = field === "quantity" || field === "price_per_unit" ? parseFloat(value) || 0 : value;
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { description: "", quantity: 1, price_per_unit: 0 }]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file && photoPreviews.length < 2) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreviews((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const removePhoto = (index) => {
    setPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.client_name || !formData.deadline) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (items.length === 0 || items.some(item => !item.description || item.quantity <= 0)) {
      toast.error("Please add at least one valid item");
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        ...formData,
        photos: photoPreviews,
        items: items.map(item => ({
          ...item,
          subtotal: item.quantity * item.price_per_unit
        })),
        down_payment: downPayment,
        down_payment_date: downPayment > 0 ? downPaymentDate : null,
        mode_of_payment: modeOfPayment,
        production_notes: productionNotes
      };

      const response = await axios.post(`${API}/orders`, orderData);
      toast.success("Order created successfully!");
      // Pass the new order back to the parent so it can be added immediately
      onOrderCreated(response.data);
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error(error.response?.data?.detail || "Failed to create order");
    } finally {
      setLoading(false);
    }
  };

  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalAmount = items.reduce((sum, item) => sum + (item.quantity * item.price_per_unit), 0);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="add-order-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading font-bold">Add New Order</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client Information */}
          <div>
            <h3 className="text-lg font-heading font-semibold mb-4">Client Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="client_name">Client Name *</Label>
                <Input
                  id="client_name"
                  value={formData.client_name}
                  onChange={(e) => handleInputChange("client_name", e.target.value)}
                  required
                  data-testid="input-client-name"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  data-testid="input-email"
                />
              </div>
              <div>
                <Label htmlFor="contact_number">Contact Number</Label>
                <Input
                  id="contact_number"
                  value={formData.contact_number}
                  onChange={(e) => handleInputChange("contact_number", e.target.value)}
                  data-testid="input-contact-number"
                />
              </div>
            </div>
          </div>

          {/* Order Details */}
          <div>
            <h3 className="text-lg font-heading font-semibold mb-4">Order Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="deadline">Deadline *</Label>
                <Input
                  id="deadline"
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => handleInputChange("deadline", e.target.value)}
                  required
                  data-testid="input-deadline"
                />
              </div>
              <div>
                <Label htmlFor="printing_method">Printing Method</Label>
                <Select
                  value={formData.printing_method}
                  onValueChange={(value) => handleInputChange("printing_method", value)}
                >
                  <SelectTrigger data-testid="select-printing-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Sublimation">Sublimation</SelectItem>
                    <SelectItem value="Embroidery">Embroidery</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="product_type">Product Type</Label>
                <Select
                  value={formData.product_type}
                  onValueChange={(value) => handleInputChange("product_type", value)}
                >
                  <SelectTrigger data-testid="select-product-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Polo">Polo</SelectItem>
                    <SelectItem value="Roundneck">Roundneck</SelectItem>
                    <SelectItem value="Jersey">Jersey</SelectItem>
                    <SelectItem value="Shorts">Shorts</SelectItem>
                    <SelectItem value="Jacket">Jacket</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="kind_of_fabric">Kind of Fabric</Label>
                <Input
                  id="kind_of_fabric"
                  value={formData.kind_of_fabric}
                  onChange={(e) => handleInputChange("kind_of_fabric", e.target.value)}
                  data-testid="input-fabric"
                />
              </div>
              <div>
                <Label htmlFor="collar_type">Collar Type</Label>
                <Input
                  id="collar_type"
                  value={formData.collar_type}
                  onChange={(e) => handleInputChange("collar_type", e.target.value)}
                  data-testid="input-collar"
                />
              </div>
              <div>
                <Label htmlFor="placket_style">Placket Style</Label>
                <Input
                  id="placket_style"
                  value={formData.placket_style}
                  onChange={(e) => handleInputChange("placket_style", e.target.value)}
                  data-testid="input-placket"
                />
              </div>
              <div>
                <Label htmlFor="button_color">Button Color</Label>
                <Input
                  id="button_color"
                  value={formData.button_color}
                  onChange={(e) => handleInputChange("button_color", e.target.value)}
                  data-testid="input-button-color"
                />
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-semibold">Order Items</h3>
              <Button type="button" onClick={addItem} size="sm" variant="outline" data-testid="add-item-button">
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </div>
            <div className="space-y-3">
              {items.map((item, index) => (
                <div key={index} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label>Description</Label>
                    <Input
                      value={item.description}
                      onChange={(e) => handleItemChange(index, "description", e.target.value)}
                      placeholder="e.g., XS - White"
                      data-testid={`item-description-${index}`}
                    />
                  </div>
                  <div className="w-24">
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                      data-testid={`item-quantity-${index}`}
                    />
                  </div>
                  <div className="w-32">
                    <Label>Price/Unit</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.price_per_unit}
                      onChange={(e) => handleItemChange(index, "price_per_unit", e.target.value)}
                      data-testid={`item-price-${index}`}
                    />
                  </div>
                  <div className="w-32">
                    <Label>Subtotal</Label>
                    <Input
                      value={formatCurrency(item.quantity * item.price_per_unit)}
                      readOnly
                      className="bg-secondary"
                      data-testid={`item-subtotal-${index}`}
                    />
                  </div>
                  {items.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeItem(index)}
                      className="text-red-600"
                      data-testid={`remove-item-${index}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4 p-4 bg-secondary rounded-md">
              <div className="flex justify-between text-sm">
                <span>Total Quantity:</span>
                <span className="font-semibold" data-testid="total-quantity">{totalQuantity}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span>Total Amount:</span>
                <span className="font-semibold text-green-600" data-testid="total-amount">
                  {formatCurrency(totalAmount)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div>
            <h3 className="text-lg font-heading font-semibold mb-4">Payment Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="total_amount">Total Amount</Label>
                <Input
                  id="total_amount"
                  value={formatCurrency(totalAmount)}
                  readOnly
                  className="bg-secondary font-semibold text-lg"
                  data-testid="display-total-amount"
                />
              </div>
            </div>
            
            {/* Down Payment Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="down_payment">Down Payment</Label>
                <Input
                  id="down_payment"
                  type="number"
                  min="0"
                  step="0.01"
                  value={downPayment}
                  onChange={(e) => setDownPayment(parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  data-testid="input-down-payment"
                />
              </div>
              <div>
                <Label htmlFor="down_payment_date">Down Payment Date</Label>
                <Input
                  id="down_payment_date"
                  type="date"
                  value={downPaymentDate}
                  onChange={(e) => setDownPaymentDate(e.target.value)}
                  data-testid="input-down-payment-date"
                />
              </div>
            </div>

            {/* Balance and Status Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="balance">Balance</Label>
                <Input
                  id="balance"
                  value={formatCurrency(Math.max(0, totalAmount - downPayment))}
                  readOnly
                  className="bg-secondary font-semibold text-lg"
                  data-testid="display-balance"
                />
              </div>
              <div>
                <Label htmlFor="payment_status">Payment Status</Label>
                <Input
                  id="payment_status"
                  value={
                    downPayment >= totalAmount && totalAmount > 0
                      ? "Paid"
                      : downPayment > 0
                      ? "Partial"
                      : "Unpaid"
                  }
                  readOnly
                  className="bg-secondary font-semibold"
                  data-testid="display-payment-status"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="mode_of_payment">Mode of Payment</Label>
              <Input
                id="mode_of_payment"
                value={modeOfPayment}
                onChange={(e) => setModeOfPayment(e.target.value)}
                placeholder="e.g., Cash, GCash, Bank Transfer, etc."
                data-testid="input-mode-of-payment"
              />
            </div>
          </div>

          {/* Production Notes */}
          <div>
            <h3 className="text-lg font-heading font-semibold mb-4">Production Notes</h3>
            <div>
              <Label htmlFor="production_notes">Notes (Optional)</Label>
              <Textarea
                id="production_notes"
                value={productionNotes}
                onChange={(e) => setProductionNotes(e.target.value)}
                placeholder="Add any special instructions or notes for production..."
                rows={4}
                data-testid="input-production-notes"
              />
              <p className="text-xs text-muted-foreground mt-1">
                These notes will appear on the job order
              </p>
            </div>
          </div>

          {/* Photo Upload */}
          <div>
            <h3 className="text-lg font-heading font-semibold mb-4">Product Photos (up to 2)</h3>
            <div className="flex flex-wrap gap-4">
              {photoPreviews.map((photo, index) => (
                <div key={index} className="relative">
                  <img src={photo} alt={`Product ${index + 1}`} className="w-32 h-32 object-cover rounded-md" loading="lazy" data-testid={`photo-preview-${index}`} />
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    data-testid={`remove-photo-button-${index}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {photoPreviews.length < 2 && (
                <label className="border-2 border-dashed border-border rounded-md w-32 h-32 flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-secondary/50 transition-colors">
                  <Upload className="h-6 w-6 text-muted-foreground mb-1" />
                  <p className="text-xs text-muted-foreground">Add Photo</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    data-testid="photo-upload-input"
                  />
                </label>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">{photoPreviews.length}/2 photos uploaded</p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-button">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="bg-primary" data-testid="submit-order-button">
              {loading ? "Creating..." : "Create Order"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
